/** Import types */
import type {DesignCenterTabProps} from "../Interfaces.tsx";
import type {
    Portfolio,
    PortfolioInput,
    PortfolioRow,
    PortfolioRowInput,
    TimeSeries,
    Variable,
    UUID
} from "../../base/ui_general/GeneralInterfaces.tsx";

import {RBSCard, RBSCardVariants} from "../../base/rbs_components/RBSCard";
import {TabCardHeader} from './components/TabCardHeader';
import {
    BaseComponent,
    type BaseComponentState,
    BaseComponentStateInitial
} from "../../base/ui_general/BaseComponent.tsx";
import {formatEuro, formatPercent, createSuccessFeedbackMessage} from "../../base/utils/GeneralUtilities";
import {PortfolioRowDetailModal} from "../modals/PortfolioRowDetailModal.tsx";
import {PortfolioDetailModal} from "../modals/PortfolioDetailModal.tsx";
import {InputStateControl} from "../../base/utils/InputStateControl";
import {RBSButton, RBSButtonVariants, RBSButtonSizes} from "../../base/rbs_components/RBSButton";
import {
    getRequest_create_portfolio_row,
    getRequest_update_portfolio_row,
    getRequest_delete_portfolio_row
} from "../../api/portfolio_row";
import {
    getRequest_create_portfolio,
    getRequest_update_portfolio,
    getRequest_delete_portfolio
} from "../../api/portfolio.tsx";
import {ApiEndPoints, LabelTextMessageTypes, PortfolioTypeEnum} from "../../base/Settings";
import autobind from "autobind-decorator";
import styles from './TabShared.module.css';
import {EmptyStateRow} from './components/EmptyStateRow';


/**
 * Props for the AssetsTab component.
 */
interface AssetsTabProps extends DesignCenterTabProps {
    /** List of portfolios to be displayed and managed. */
    portfolios: Array<Portfolio>;
    /** List of time series available for selection. */
    time_series: Array<TimeSeries>;
    /** List of variables available for selection. */
    variables: Array<Variable>;
}

/**
 * State for the AssetsTab component.
 */
interface AssetsTabState extends BaseComponentState {
    /** Whether the PortfolioRow modal is visible. */
    showPortfolioRowModal: boolean;
    /** Whether the PortfolioRow modal form is valid. */
    portfolioRowModalValid: boolean;
    /** The PortfolioRow currently being edited, if any. */
    editingPortfolioRow: PortfolioRow | undefined;
    /** The Portfolio currently being edited or added to. */
    activePortfolio: Portfolio | undefined;
    /** Whether the Portfolio modal is visible. */
    showPortfolioModal: boolean;
    /** Whether the Portfolio modal form is valid. */
    portfolioModalValid: boolean;
    /** The Portfolio currently being edited, if any. */
    editingPortfolio: Portfolio | undefined;
    /** Currently hovered portfolio row index (for hover styling) */
    hoveredRow: number | undefined;
}

/**
 * AssetsTab Component
 */
export class AssetsTab extends BaseComponent<AssetsTabProps, AssetsTabState> {
    private readonly portfolioRowInputStateControl: InputStateControl;
    private readonly portfolioInputStateControl: InputStateControl;

    constructor(props: AssetsTabProps) {
        super(props);

        // Initial state for modals and editing objects
        this.state = {
            ...BaseComponentStateInitial,
            showPortfolioRowModal: false,
            portfolioRowModalValid: true,
            editingPortfolioRow: undefined,
            activePortfolio: undefined,

            showPortfolioModal: false,
            portfolioModalValid: false,
            editingPortfolio: undefined,
            hoveredRow: undefined
        };

        // InputStateControl tracks validity of modal forms
        this.portfolioRowInputStateControl = new InputStateControl();
        this.portfolioRowInputStateControl.setGlobalValidityUpdateCallback(this.handlePortfolioRowModalValidityUpdate);

        this.portfolioInputStateControl = new InputStateControl();
        this.portfolioInputStateControl.setGlobalValidityUpdateCallback(this.handlePortfolioModalValidityUpdate);
    }

    /**
     * Updates the validity state of the PortfolioRow modal.
     * Triggered by the PortfolioRow InputStateControl.
     */
    @autobind
    handlePortfolioRowModalValidityUpdate(isValid: boolean) {
        this.setState({portfolioRowModalValid: isValid});
    }

    /**
     * Updates the validity state of the Portfolio modal.
     * Triggered by the Portfolio InputStateControl.
     */
    @autobind
    handlePortfolioModalValidityUpdate(isValid: boolean) {
        this.setState({portfolioModalValid: isValid});
    }

    /**
     * Handles submission of the PortfolioRow modal.
     */
    @autobind
    handlePortfolioRowModalSubmit(portfolioRowInput: PortfolioRowInput) {
        if (!this.props.transaction || !this.props.transaction.id) return;
        if (!this.state.activePortfolio) return;

        portfolioRowInput.portfolio_id = this.state.activePortfolio.id;

        if (this.state.editingPortfolioRow) {
            const updatedRow = {...this.state.editingPortfolioRow, ...portfolioRowInput};
            this.processFetchRequest(
                getRequest_update_portfolio_row(updatedRow),
                () => {
                    this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.PORTFOLIO_ROW_CREATED));
                    this.props.loadData([ApiEndPoints.PORTFOLIO]);
                    this.setState({showPortfolioModal: false, editingPortfolio: undefined});
                }
            );
        } else {
            this.processFetchRequest(
                getRequest_create_portfolio_row(portfolioRowInput),
                () => {
                    this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.PORTFOLIO_ROW_CREATED));
                    this.props.loadData([ApiEndPoints.PORTFOLIO]);
                    this.setState({showPortfolioModal: false, editingPortfolio: undefined});
                }
            );
        }
    }

    /**
     * Handles submission of the Portfolio modal.
     */
    @autobind
    handlePortfolioModalSubmit(portfolioInput: PortfolioInput) {
        if (!this.props.transaction || !this.props.transaction.id) return;

        const payload = {
            ...portfolioInput,
            portfolio_type: portfolioInput.portfolio_type ?? PortfolioTypeEnum.MAIN,  //TODO: Is that not required part of PortfolioINPUT?
            transaction_id: this.props.transaction.id
        };

        if (this.state.editingPortfolio) {
            //TODO: Move the stripping of portfolio_rows into the corresponding getFetchRequest... routine
            //TODO: The same applies to TimeSeries, Triggers, Waterfalls, Formulas...
            const {portfolio_rows, ...cleanedEditing} = this.state.editingPortfolio;

            const updatedPortfolio: Portfolio = {
                ...cleanedEditing,
                ...payload,
                portfolio_rows: []
            };

            this.processFetchRequest(
                getRequest_update_portfolio(updatedPortfolio),
                () => {
                    this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.PORTFOLIO_UPDATED));
                    this.props.loadData([ApiEndPoints.PORTFOLIO]);
                    this.setState({showPortfolioModal: false, editingPortfolio: undefined});
                }
            );
        } else {
            this.processFetchRequest(
                getRequest_create_portfolio(payload),
                () => {
                    this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.PORTFOLIO_CREATED));
                    this.props.loadData([ApiEndPoints.PORTFOLIO]);
                    this.setState({showPortfolioModal: false});
                }
            );
        }
    }

    /**
     * Handles the deletion of a portfolio row by its UUID via an API request.
     * @param portfolio_row_id The UUID of the portfolio row to delete.
     */
    @autobind
    handleDeletePortfolioRow(portfolio_row_id: UUID) {
        this.processFetchRequest(
            getRequest_delete_portfolio_row(portfolio_row_id),
            () => {
                this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.PORTFOLIO_ROW_DELETED));
                this.props.loadData([ApiEndPoints.PORTFOLIO]);
            }
        );
    }

    /**
     * Handles the deletion of a portfolio by its UUID via an API request.
     * @param portfolio_id The UUID of the portfolio to delete.
     */
    @autobind
    handleDeletePortfolio(portfolio_id: UUID) {
        this.processFetchRequest(
            getRequest_delete_portfolio(portfolio_id),
            () => {
                this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.PORTFOLIO_DELETED));
                this.props.loadData([ApiEndPoints.PORTFOLIO]);
            }
        );
    }

    render() {
        // Helper to get the name of a TimeSeries by ID
        const getTimeSeriesName = (id: string | undefined) => {
            if (!id || !this.props.time_series) return "---";
            const ts = this.props.time_series.find(t => t.id === id);
            return ts ? ts.name : "---";
        };

        return (
            <div className={styles.tabContainer}>
                <div className={styles.scrollArea}>
                    {/* Render each Portfolio as a card */}
                    <div className={styles.cardStack}>
                        {this.props.portfolios.map((portfolio, idx) => (
                            <RBSCard
                                key={idx}
                                header={
                                    <TabCardHeader
                                        icon={"📁"}
                                        title={<>
                                            {portfolio.name}
                                            {portfolio.portfolio_type && (
                                                <span
                                                    className={styles.highlightTag}>[{portfolio.portfolio_type}]</span>
                                            )}
                                        </>}
                                        actions={<div>
                                            <RBSButton
                                                label="Add Row"
                                                onClick={() => this.setState({
                                                    showPortfolioRowModal: true,
                                                    editingPortfolioRow: undefined,
                                                    activePortfolio: portfolio
                                                })}
                                                variant={RBSButtonVariants.PRIMARY}
                                                size={RBSButtonSizes.SMALL}
                                            />
                                            <RBSButton
                                                label="Edit"
                                                onClick={() => this.setState({
                                                    showPortfolioModal: true,
                                                    editingPortfolio: portfolio
                                                })}
                                                variant={RBSButtonVariants.SECONDARY}
                                                size={RBSButtonSizes.SMALL}
                                                extraStyle={{marginLeft: "0.5rem"}}
                                            />
                                            <RBSButton
                                                label="Delete"
                                                title="Delete Portfolio"
                                                onClick={() => this.handleDeletePortfolio(portfolio.id)}
                                                variant={RBSButtonVariants.DANGER}
                                                size={RBSButtonSizes.SMALL}
                                                extraStyle={{marginLeft: "0.5rem"}}
                                            />
                                        </div>}
                                    />
                                }
                                header_class_name={styles.cardHeaderPrimary}
                                background={RBSCardVariants.Light}
                                border={RBSCardVariants.Primary}
                                body_class_name={styles.cardBodyPanel}
                                container_class_name={styles.roundedCard}
                            >
                                {/* Table displaying all PortfolioRows */}
                                <div className={styles.tableScrollX}>
                                    <table className={styles.dataTable}>
                                        <thead>
                                        <tr>
                                            <th className={styles.headerPadLg}>Maturity</th>
                                            <th className={styles.headerPadLg}>PD</th>
                                            <th className={styles.headerPadLg}>LGD</th>
                                            <th className={styles.headerPadLg}>Initial LGD</th>
                                            <th className={styles.headerPadLg}>Recovery Lag</th>
                                            <th className={styles.headerPadLg}>Asset Corr.</th>
                                            <th className={`${styles.headerPadLg} ${styles.colNarrowSm}`}>Amortization</th>
                                            <th className={`${styles.headerPadLg} ${styles.colNarrowSm}`}>Initial Notional</th>
                                            <th className={styles.headerPadLg}>Spread</th>
                                            <th className={styles.headerPadLg}>Base Curve</th>
                                            <th className={styles.headerPadLg}>CPR</th>
                                            <th className={`${styles.headerActionsNarrow} ${styles.headerPadLg}`}></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {portfolio.portfolio_rows.map((row, rIdx) => (
                                            <tr
                                                key={rIdx}
                                                className={this.state.hoveredRow === rIdx ? styles.tableRowHover : ''}
                                                onMouseEnter={() => this.setState({hoveredRow: rIdx})}
                                                onMouseLeave={() => this.setState({hoveredRow: undefined})}
                                            >
                                                <td className={`${styles.cellCenter} ${styles.cellPadLg}`}>{row.maturity}</td>
                                                <td className={`${styles.cellCenter} ${styles.cellPadLg}`}>{formatPercent(row.pd)}</td>
                                                <td className={`${styles.cellCenter} ${styles.cellPadLg}`}>{formatPercent(row.lgd)}</td>
                                                <td className={`${styles.cellCenter} ${styles.cellPadLg}`}>{formatPercent(row.initial_lgd)}</td>
                                                <td className={`${styles.cellCenter} ${styles.cellPadLg}`}>{row.recovery_lag}</td>
                                                <td className={`${styles.cellCenter} ${styles.cellPadLg}`}>{formatPercent(row.asset_correlation)}</td>
                                                <td className={`${styles.cellCenter} ${styles.cellPadLg}`}>{row.amortization_type}</td>
                                                <td className={`${styles.cellCenter} ${styles.cellPadLg}`}>{formatEuro(row.initial_notional)}</td>
                                                <td className={`${styles.cellCenter} ${styles.cellPadLg}`}>{formatPercent(row.spread)}</td>
                                                <td className={`${styles.cellCenter} ${styles.cellPadLg}`}>{getTimeSeriesName(row.base_curve_id)}</td>
                                                <td className={`${styles.cellCenter} ${styles.cellPadLg}`}>{formatPercent(row.cpr)}</td>
                                                <td className={styles.actionCell}>
                                                    <div className={styles.hoverButtons}>
                                                        <RBSButton
                                                            label="✏️"
                                                            title="Edit"
                                                            onClick={() =>
                                                                this.setState({
                                                                    showPortfolioRowModal: true,
                                                                    editingPortfolioRow: row,
                                                                    activePortfolio: portfolio
                                                                })
                                                            }
                                                            variant={RBSButtonVariants.SECONDARY}
                                                            size={RBSButtonSizes.SMALL}
                                                        />
                                                        <RBSButton
                                                            label="🗑️"
                                                            title="Delete"
                                                            onClick={() => {
                                                                this.handleDeletePortfolioRow(row.id);
                                                            }}
                                                            variant={RBSButtonVariants.DANGER}
                                                            size={RBSButtonSizes.SMALL}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {(!portfolio.portfolio_rows || portfolio.portfolio_rows.length === 0) && (
                                            <EmptyStateRow inTable colSpan={12}
                                                           message={'No rows yet. Use "Add Row" to get started.'}/>
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            </RBSCard>
                        ))}
                    </div>
                    <div className={styles.toolbarBottom}>
                        <RBSButton
                            label="Add Portfolio"
                            variant={RBSButtonVariants.PRIMARY}
                            size={RBSButtonSizes.LARGE}
                            onClick={() => this.setState({showPortfolioModal: true, editingPortfolio: undefined})}
                            title="Add Portfolio"
                        />
                    </div>
                </div>

                {/* PortfolioRow modal for adding/editing rows */}
                <PortfolioRowDetailModal
                    show={this.state.showPortfolioRowModal}
                    onClose={() => this.setState({showPortfolioRowModal: false})}
                    onSubmit={(input) => this.handlePortfolioRowModalSubmit(input)}
                    portfolioRow={this.state.editingPortfolioRow}
                    isValid={this.state.portfolioRowModalValid}
                    inputStateControl={this.portfolioRowInputStateControl}
                    user_language={this.props.user_language}
                    time_series={this.props.time_series}
                    currentPortfolioId={this.state.activePortfolio?.id ?? ""}
                />

                {/* Portfolio modal for adding/editing portfolios */}
                <PortfolioDetailModal
                    show={this.state.showPortfolioModal}
                    onClose={() => this.setState({showPortfolioModal: false, editingPortfolio: undefined})}
                    onSubmit={(input) => this.handlePortfolioModalSubmit(input as PortfolioInput)}
                    portfolio={this.state.editingPortfolio}
                    isValid={this.state.portfolioModalValid}
                    inputStateControl={this.portfolioInputStateControl}
                    user_language={this.props.user_language}
                />
            </div>
        );
    }
}
